/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { Component } from 'react';
import { WrappedFieldMetaProps } from 'redux-form';
import { css, StyleSheet } from 'aphrodite';
import { a11y } from '@blend/consumer-components';

import { BlIcon, BlIconSize, BlIconType, keyCodes } from '@blend/shared-react';
import { getAutocompleteResults, isClearCharacter } from '../addressUtils';
import { addressUtils, googlePlacesUtils } from '../utils';

import AutocompletePrediction from '../../../../../types/autocompletePrediction';
import InputContainer from '../../../TaskInputs/InputContainer';
import { TextInputWrapper, TextInputWrapperConfig } from '../../../TaskInputs/TextInputWrapper';
import { InputConfig } from '../../../TaskInputs/types';
import { TaskFieldInputProps, AddressInput } from '../types';
import { supportUSAndCanadaAddressInputs } from '../config';
import AddressDropdown from './AddressDropdown';
import { FormattedAddress } from './addressDropdownTypes';
import { diagnostics } from '@utils/diagnostics';
import { noop, isEmpty, includes, size } from '@utils/lodash';

const styles = StyleSheet.create({
  dropdownWrapper: {
    top: -22,
    marginTop: 10,
    position: 'relative',
  },
});

export interface OnAddressSelectedOpts {
  skipModal: boolean;
}

/**
 * Desired behavior:
 * - Initializes to empty, and typing searches.
 * - Selecting an address (potentially invalid) renders that address to a string temporarily while the modal is open.
 * - When the modal is submitted with a valid address, render that valid address.
 * - Focusing the text input again results in maintaining the same string, but:
 *   * Hitting delete clears the input and shows the previous value as a dropdown option.
 *   * Typing a key means that *only that key* will be in the text box, and it shows search results as dropdown options.
 *     If the search string is deleted, it goes back to showing the previous value as the dropdown option.
 */

type ExternalOnSelect = (address: AddressInput.Address, opts?: OnAddressSelectedOpts) => void;

export interface AddressAutocompleteDropdownProps {
  selectedValue: AddressInput.Address | {};
  existingAddresses: AddressInput.Address[];
  onSelect: ExternalOnSelect;
  label: string;
  name: string;
  withCounty: boolean;
  error?: string;
  placeholder?: string;
  showSearchIcon: boolean;
  disabled?: boolean;
  isCityOnlyInput?: boolean;
}

interface AddressAutocompleteDropdownState {
  displayText: string | null;
  dropdownOpen: boolean;
  searchResults: AutocompletePrediction[];
  searchText: string | null;
  selectedAddressIndex: number;
}

type RenderedAddressType = 'dm_addresses' | 'api_addresses' | 'selected_address';

const addressTypeToIconMap: { [key in RenderedAddressType]: BlIconType } = {
  dm_addresses: 'location',
  api_addresses: 'location',
  selected_address: 'location',
};

class AddressAutocompleteDropdown extends Component<
  AddressAutocompleteDropdownProps,
  AddressAutocompleteDropdownState
> {
  textInput: any;
  textInputHandlers: TaskFieldInputProps;
  htmlId: string;
  autocompleteSessionToken: google.maps.places.AutocompleteSessionToken;

  constructor(props: AddressAutocompleteDropdownProps) {
    super(props);

    const textInputConfig = props.showSearchIcon
      ? { inputPrefix: <BlIcon icon="search" />, prefixSize: 'md' as BlIconSize }
      : {};
    this.textInput = TextInputWrapper(textInputConfig as TextInputWrapperConfig);
    // this.htmlId = formEscapingUtils.getIdFromName(props.name);
    this.htmlId = props.name;
    this.autocompleteSessionToken = googlePlacesUtils.generateNewSessionToken();

    this.state = {
      selectedAddressIndex: -1,
      displayText: '',
      searchText: '',
      dropdownOpen: false,
      searchResults: [],
    };

    this.textInputHandlers = {
      name: props.name,
      onBlur: () => this.setState({ dropdownOpen: false }),
      onClick: () => {
        if (!googlePlacesUtils.googlePlacesEnabled()) {
          props.onSelect({} as AddressInput.Address);
        }
      },
      onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
        // React cleans up events for performance reasons, but we need to keep this one around in order to have
        // a reference to the element to set the selection on it.
        event.persist();
        const searchText = this.state.searchText || this.selectedAddressString;
        this.setState({ dropdownOpen: true, searchText }, () => {
          const inputElement = event.target as HTMLInputElement;
          inputElement.setSelectionRange(0, inputElement.value.length);
        });
        if (this.selectedAddressString) {
          this.setState({ searchText: this.selectedAddressString, displayText: this.selectedAddressString });
        }
      },
      onDragStart: noop,
      onDrop: noop,
      onDragEnd: noop,
      onKeyDown: this.onKeyDown,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.defaultPrevented) {
          return;
        }
        this.updateSearchText(e.target.value);
      },
      value: this.state.searchText,
    };
  }

  get dataModelAddresses() {
    const { existingAddresses } = this.props;
    return existingAddresses;
  }

  get apiAddresses() {
    const apiAddresses = this.state.searchResults;

    // We only want to append this if we have few enough addresses AND we have searchText present;
    // if searchText isn't present, it could mean that the user just submitted the modal and we're refocusing
    // on the address input for a11y reasons, but don't want to show this "confirmation" list element.
    if (apiAddresses.length < 5 && this.state.searchText) {
      return apiAddresses.concat([
        {
          apiResult: null,
          context: 'Confirm address:',
          description: this.state.searchText,
        },
      ]);
    }

    return apiAddresses;
  }

  // This will only ever return one address, but we wrap it in an array so its signature
  // is like the other two.
  get selectedValueAddresses() {
    const { selectedValue } = this.props;
    if (isEmpty(selectedValue)) {
      return [];
    }
    return [selectedValue as AddressInput.Address];
  }

  get selectedAddressString() {
    const { selectedValue } = this.props;
    return addressUtils.dataModelAddressToString(selectedValue as AddressInput.Address);
  }

  queryAndPopulateAddresses = async (searchText: string) => {
    const { isCityOnlyInput } = this.props;
    const results = await getAutocompleteResults(searchText, this.autocompleteSessionToken, isCityOnlyInput);
    this.setState({ searchResults: results });
  };

  updateSearchText = (newText: string | null) => {
    this.setState({ displayText: newText, searchText: newText });
    this.queryAndPopulateAddresses(newText || '');
  };

  clearLocalStateAndPropagateAddressChange: ExternalOnSelect = (address, opts) => {
    const { onSelect } = this.props;
    this.setState({ searchText: this.state.displayText, searchResults: [] });
    onSelect(address, opts);
  };

  /**
   * This method is responsible for taking the user input and calling back up to the parent with a parsed
   * address that looks like a data model. There are a few scenarios for this, listed below.
   */
  selectAddress = async () => {
    this.setState({ dropdownOpen: false });
    const { onSelect } = this.props;
    // We make the assumption that if a selected address is exposed, it's populated. Let's validate that here.
    if (this.getRenderedAddressType() === 'selected_address' && isEmpty(this.selectedValueAddresses)) {
      diagnostics.notifyInvalidState('Selected address exposed with no valid value.');
    }

    // 1) We have no results for the currently exposed list and we need to parse the searchText and pass it up.
    const exposedEmptyDMList = this.getRenderedAddressType() === 'dm_addresses' && isEmpty(this.dataModelAddresses);
    const exposedEmptyAPIList = this.getRenderedAddressType() === 'api_addresses' && isEmpty(this.apiAddresses);
    if (exposedEmptyDMList || exposedEmptyAPIList) {
      const parsedAddress = addressUtils.parseAddressFromString(this.state.searchText || '');
      return this.clearLocalStateAndPropagateAddressChange(parsedAddress);
    }
    // 2) We have a selected result or no selected result and the API list is exposed.
    if (this.getRenderedAddressType() === 'api_addresses' && !isEmpty(this.apiAddresses)) {
      // The input address will be parsed when API addresses is not selected
      if (this.state.selectedAddressIndex < 0) {
        const parsedAddress = addressUtils.parseAddressFromString(this.state.searchText || undefined);
        return this.clearLocalStateAndPropagateAddressChange(parsedAddress);
      }
      const selectedAPIAddress = this.apiAddresses[this.state.selectedAddressIndex];
      // This an address that didn't show up through Google, and the user is choosing to enter it manually.
      if (!selectedAPIAddress.apiResult) {
        const parsedAddress = addressUtils.parseAddressFromString(this.state.searchText || undefined);
        return this.clearLocalStateAndPropagateAddressChange(parsedAddress);
      }

      try {
        const placeDetails = await googlePlacesUtils.getFullAddress(
          selectedAPIAddress.apiResult,
          this.autocompleteSessionToken,
        );
        // Google Places sessions can only do one place details request, so we need a new one
        this.autocompleteSessionToken = googlePlacesUtils.generateNewSessionToken();

        const dmAddress = addressUtils.parseAPIAddress(placeDetails, supportUSAndCanadaAddressInputs);

        // for now, always delete county
        // if (withCounty && dmAddress.countyName) {
        //   dmAddress.countyName = await getValidCountyName(
        //     dmAddress.countyName,
        //     dmAddress.stateCode || '',
        //   );
        // } else {
        //   // Delete the countyName to avoid populating the data model with invalid data.
        //   delete dmAddress.county;
        // }

        delete dmAddress.county;

        return this.clearLocalStateAndPropagateAddressChange(dmAddress);
      } catch (err) {
        // Google Places sessions can only do one place details request, so we need a new one
        this.autocompleteSessionToken = googlePlacesUtils.generateNewSessionToken();
        const parsedAddress = addressUtils.parseAddressFromString(selectedAPIAddress.description);
        return this.clearLocalStateAndPropagateAddressChange(parsedAddress);
      }
    }

    // 3) We have a selected result or no selected result and the DM list is exposed.
    if (this.getRenderedAddressType() === 'dm_addresses' && !isEmpty(this.dataModelAddresses)) {
      // The input address will be parsed when Data Model address is not selected
      const selectedDMAddress = this.dataModelAddresses[this.state.selectedAddressIndex];

      // Explicitly, we only want to skip the modal when a user is choosing a known valid, prepopulated address.
      return this.clearLocalStateAndPropagateAddressChange(selectedDMAddress, { skipModal: true });
    }

    // 4) We have a selected result or no selected result and a previous address is exposed.
    if (this.getRenderedAddressType() === 'selected_address') {
      const selectedAddress = this.selectedValueAddresses[0];
      return this.clearLocalStateAndPropagateAddressChange(selectedAddress);
    }

    // 5) No idea. Populate it as empty!
    onSelect({} as AddressInput.Address);
  };

  getRenderedAddressType = () => {
    const { selectedValue } = this.props;

    const searchTextIsSelectedValue =
      this.state.searchText &&
      !isEmpty(selectedValue) &&
      this.state.searchText === addressUtils.dataModelAddressToString(selectedValue as AddressInput.Address);

    // If the searchText is set to null, it means that the address field was cleared by the user; we want to
    // show the address they cleared while the search field is still empty.
    //
    // If the search text is the same as the selected value, we're showing a preview of the search text
    // because the user has already filled out the input. We want to handle it as a selected address
    // so they can re-open the modal and edit it if they want to!
    if (this.state.searchText === null || searchTextIsSelectedValue) {
      return 'selected_address';
    }

    // If we're just searching, we want the ones we pulled back from the API.
    if (this.state.searchText) {
      return 'api_addresses';
    }

    // Otherwise, show other valid addresses that are available on the data model.
    return 'dm_addresses';
  };

  getFormattedAddresses = (): FormattedAddress[] => {
    const typeToRender = this.getRenderedAddressType();
    switch (typeToRender) {
      case 'api_addresses':
        return addressUtils.formatAPIAddresses(this.apiAddresses);
      case 'dm_addresses':
        return addressUtils.formatDMAddresses(this.dataModelAddresses);
      case 'selected_address':
        return addressUtils.formatDMAddresses(this.selectedValueAddresses);
      default:
        throw new Error(`Unknown address render type: ${typeToRender}`);
    }
  };

  getSearchListLength = () => {
    const lengthMap = {
      api_addresses: this.apiAddresses.length,
      dm_addresses: this.dataModelAddresses.length,
      selected_address: this.selectedValueAddresses.length,
    };

    const currentSearchListLength = lengthMap[this.getRenderedAddressType()];
    return currentSearchListLength;
  };

  onKeyDown: React.EventHandler<React.KeyboardEvent<HTMLInputElement>> = e => {
    const { DOWN_ARROW, UP_ARROW, ENTER, TAB } = keyCodes;
    const KEY = e.keyCode || e.which;
    // close dropdown so the following blur event doesn't target the dropdown
    if (KEY === TAB) {
      return this.setState({ dropdownOpen: false });
    }

    // There's a value populated in the box and the user's trying to interact with it.
    if (this.selectedAddressString && this.state.searchText === this.selectedAddressString && isClearCharacter(KEY)) {
      // Prevent default so the input's onChange handler doesn't try to act on this as well.
      e.preventDefault();

      // set the searchText to null to indicate to the rest of the component that the address field was cleared
      // intentionally by the user and we want to expose the `selected_address` functionality.
      return this.updateSearchText(null);
    }

    if (includes([DOWN_ARROW, UP_ARROW, ENTER], KEY)) {
      e.preventDefault();

      if (includes([DOWN_ARROW, UP_ARROW], KEY)) {
        const currentSearchListLength = this.getSearchListLength();
        const nextIndex = addressUtils.getNewSelectedIndex(
          this.state.selectedAddressIndex,
          currentSearchListLength,
          KEY,
        );
        // when one of the api addresses is selected, address will be prefilled with the api address. otherwise it will
        // be filled with searchText
        if (nextIndex < 0) {
          this.setState({ displayText: this.state.searchText });
        }
        if (this.apiAddresses[nextIndex]) {
          this.setState({ displayText: this.apiAddresses[nextIndex].description });
        }

        return this.setState({
          selectedAddressIndex: nextIndex,
        });
      }
      // Modal will not show when empty input and there is not a dropdown
      if (this.getSearchListLength() && (this.state.searchText || this.state.selectedAddressIndex >= 0)) {
        return this.selectAddress();
      }
    }
  };

  onSelectAddress = (newIndex: number) => {
    this.setState(
      {
        selectedAddressIndex: newIndex,
      },
      this.selectAddress,
    );
  };

  render() {
    const { error, placeholder, disabled, label } = this.props;

    const textInputMetaProps = {
      // We set touched to true always here because we handle hiding the error when *not* touched
      // above this input.
      touched: true,
      error,
    } as WrappedFieldMetaProps;

    const inputConfig = {
      type: 'text',
      required: true,
      id: this.htmlId,
      label: label || 'Address',
    } as InputConfig;

    // This is the only property in textInputHandlers that changes, so we update it here on every render.
    const userIsSearchingWithUnderlyingValue = this.state.searchText === null;
    this.textInputHandlers.value = (() => {
      if (!this.state.dropdownOpen) {
        return this.selectedAddressString;
      }
      if (userIsSearchingWithUnderlyingValue) {
        return '';
      }
      return this.state.displayText;
    })();

    const formattedAddresses = (() => {
      if (userIsSearchingWithUnderlyingValue) {
        return addressUtils.formatDMAddresses(this.selectedValueAddresses);
      }
      return this.getFormattedAddresses();
    })();

    return (
      <div className="AddressAutocompleteDropdown">
        <label htmlFor={this.htmlId} className={css(a11y.srOnly)}>
          Please type your address as Street Address, City, State Zip Code and press Enter.
        </label>
        <InputContainer
          placeholder={placeholder}
          onClick={this.textInputHandlers.onClick}
          input={this.textInputHandlers}
          inputComponent={this.textInput}
          meta={textInputMetaProps}
          required
          config={inputConfig}
          disabled={disabled}
        />
        {this.state.dropdownOpen && size(formattedAddresses) > 0 && (
          <div className={css(styles.dropdownWrapper)}>
            <AddressDropdown
              searchText={this.state.searchText || ''}
              addressList={formattedAddresses}
              selectedAddressIndex={this.state.selectedAddressIndex}
              icon={addressTypeToIconMap[this.getRenderedAddressType()]}
              onSelect={this.onSelectAddress}
              showAPICredit={this.getRenderedAddressType() === 'api_addresses'}
            />
          </div>
        )}
      </div>
    );
  }
}

export default AddressAutocompleteDropdown;
